<template>
  <el-dialog
    :title="$t('addComplaint.title')"
    :visible.sync="visible"
    width="650px"
    :close-on-click-modal="false"
    @close="handleClose"
    custom-class="complaint-modal"
  >
    <div class="modal-header">
      <span class="modal-title">{{ $t('addComplaint.title') }}</span>
    </div>
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="86px"
      class="complaint-form"
    >
      <el-form-item :label="$t('addComplaint.community')" prop="communityId" required>
        <el-select
          v-model="form.communityId"
          :placeholder="$t('addComplaint.communityPlaceholder')"
          style="width: 516px"
          @change="handleCommunityChange"
        >
          <el-option
            v-for="item in communityList"
            :key="item.communityId"
            :label="item.name"
            :value="item.communityId"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('addComplaint.type')" prop="typeCd" required>
        <el-select
          v-model="form.typeCd"
          :placeholder="$t('addComplaint.typePlaceholder')"
          style="width: 516px"
        >
          <el-option
            v-for="item in complaintTypes"
            :key="item.typeCd"
            :label="item.typeName"
            :value="item.typeCd"
          />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('addComplaint.room')" prop="roomId" required>
        <el-input
          v-model="form.roomName"
          :placeholder="$t('addComplaint.roomPlaceholder')"
          class="room-select-input"
          style="width: 516px"
          readonly
          @focus="openRoomPicker"
          @click.native="openRoomPicker"
        >
          <i slot="suffix" class="el-input__icon el-icon-arrow-down"></i>
        </el-input>
      </el-form-item>

      <el-form-item :label="$t('addComplaint.contact')" prop="complaintName" required>
        <el-input
          v-model="form.complaintName"
          :placeholder="$t('addComplaint.contactPlaceholder')"
          style="width: 516px"
        />
      </el-form-item>

      <el-form-item :label="$t('addComplaint.phone')" prop="tel" required>
        <el-input
          v-model="form.tel"
          :placeholder="$t('addComplaint.phonePlaceholder')"
          style="width: 516px"
        />
      </el-form-item>

      <el-form-item :label="$t('addComplaint.content')" prop="context" required>
        <el-input
          v-model="form.context"
          type="textarea"
          :rows="4"
          :placeholder="$t('addComplaint.contentPlaceholder')"
          style="width: 516px"
        />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button class="cancel-btn" @click="visible = false">
        {{ $t('common.cancel') }}
      </el-button>
      <el-button class="save-btn" type="primary" @click="handleSubmit" :loading="loading">
        {{ $t('addComplaint.register') }}
      </el-button>
    </div>

    <el-dialog
      :title="$t('addComplaint.selectRoomTitle')"
      :visible.sync="roomPickerVisible"
      width="760px"
      append-to-body
      custom-class="complaint-room-picker-dialog"
      :close-on-click-modal="false"
    >
      <div class="room-picker-toolbar">
        <el-input
          v-model.trim="roomSearch.roomNumLike"
          :placeholder="$t('addComplaint.roomSearchPlaceholder')"
          class="room-picker-search"
          clearable
          @keyup.enter.native="searchRooms"
        />
        <el-input
          v-model.trim="roomSearch.ownerNameLike"
          :placeholder="$t('addComplaint.ownerSearchPlaceholder')"
          class="room-picker-search"
          clearable
          @keyup.enter.native="searchRooms"
        />
        <el-input
          v-model.trim="roomSearch.linkLike"
          :placeholder="$t('addComplaint.phoneSearchPlaceholder')"
          class="room-picker-search"
          clearable
          @keyup.enter.native="searchRooms"
        />
        <el-button class="room-picker-search-btn" @click="searchRooms">
          <i class="el-icon-search"></i>{{ $t('common.search') }}
        </el-button>
      </div>
      <el-table
        v-loading="roomLoading"
        :data="roomList"
        class="room-picker-table"
        :header-cell-style="headerCellStyle"
        :cell-style="cellStyle"
      >
        <el-table-column prop="communityName" :label="$t('addComplaint.community')" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.communityName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="roomName" :label="$t('addComplaint.room')" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">{{ formatRoomName(scope.row) || '-' }}</template>
        </el-table-column>
        <el-table-column prop="ownerName" :label="$t('addComplaint.owner')" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.ownerName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="link" :label="$t('addComplaint.phone')" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.link || '-' }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" width="100">
          <template slot-scope="scope">
            <a class="room-picker-choose" @click="chooseRoom(scope.row)">{{ $t('addComplaint.choose') }}</a>
          </template>
        </el-table-column>
      </el-table>
      <div class="room-picker-pagination">
        <span class="pagination-total">共{{ roomTotal }}条</span>
        <el-pagination
          :current-page="roomPage"
          :page-size="roomPageSize"
          layout="prev, pager, next"
          :total="roomTotal"
          @current-change="handleRoomPageChange"
        />
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { listComplaintTypes, saveComplaint, queryRooms } from '@/api/oa/addComplaintApi'
import { getEffectiveCommunityId, getMyEnteredCommunitys } from '@/api/community/communityApi'

export default {
  name: 'AddComplaintModal',
  data() {
    return {
      visible: false,
      loading: false,
      roomPickerVisible: false,
      roomLoading: false,
      roomSearch: {
        roomNumLike: '',
        ownerNameLike: '',
        linkLike: ''
      },
      roomList: [],
      roomPage: 1,
      roomPageSize: 10,
      roomTotal: 0,
      communityId: '',
      communityList: [],
      complaintTypes: [],
      form: {
        communityId: '',
        typeCd: '',
        roomId: '',
        roomName: '',
        complaintName: '',
        tel: '',
        context: ''
      },
      rules: {
        communityId: [
          { required: true, message: this.$t('addComplaint.communityRequired'), trigger: 'change' }
        ],
        typeCd: [
          { required: true, message: this.$t('addComplaint.typeRequired'), trigger: 'change' }
        ],
        roomId: [
          { required: true, message: this.$t('addComplaint.roomRequired'), trigger: 'change' }
        ],
        complaintName: [
          { required: true, message: this.$t('addComplaint.contactRequired'), trigger: 'blur' },
          { max: 200, message: this.$t('addComplaint.contactMaxLength'), trigger: 'blur' }
        ],
        tel: [
          { required: true, message: this.$t('addComplaint.phoneRequired'), trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: this.$t('addComplaint.phoneFormat'), trigger: 'blur' }
        ],
        context: [
          { required: true, message: this.$t('addComplaint.contentRequired'), trigger: 'blur' },
          { max: 4000, message: this.$t('addComplaint.contentMaxLength'), trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    async open() {
      this.communityId = getEffectiveCommunityId()
      this.form = {
        communityId: getEffectiveCommunityId(this.communityId),
        typeCd: '',
        roomId: '',
        roomName: '',
        complaintName: '',
        tel: '',
        context: ''
      }
      await this.loadCommunityList()
      this.listComplaintTypes()
      this.visible = true
    },
    async loadCommunityList() {
      try {
        const res = await getMyEnteredCommunitys({ page: 1, row: 1000 })
        this.communityList = (res.communitys || []).filter(item => getEffectiveCommunityId(item.communityId))
      } catch (error) {
        console.error('获取小区列表失败:', error)
      }
    },
    async listComplaintTypes() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: getEffectiveCommunityId(this.form.communityId)
        }
        const { data } = await listComplaintTypes(params)
        this.complaintTypes = data || []
      } catch (error) {
        console.error('获取投诉类型失败:', error)
      }
    },
    handleCommunityChange() {
      this.form.typeCd = ''
      this.form.roomId = ''
      this.form.roomName = ''
      this.listComplaintTypes()
    },
    openRoomPicker() {
      if (!this.form.communityId) {
        this.$message.warning(this.$t('addComplaint.communityRequired'))
        return
      }
      this.roomPickerVisible = true
      this.roomPage = 1
      this.loadRooms()
    },
    searchRooms() {
      this.roomPage = 1
      this.loadRooms()
    },
    async loadRooms() {
      try {
        this.roomLoading = true
        const res = await queryRooms({
          page: this.roomPage,
          row: this.roomPageSize,
          communityId: this.form.communityId,
          roomNumLike: this.roomSearch.roomNumLike,
          ownerNameLike: this.roomSearch.ownerNameLike,
          linkLike: this.roomSearch.linkLike
        })
        this.roomList = res.rooms || res.data || []
        this.roomTotal = Number(res.total || 0)
      } catch (error) {
        this.$message.error(error.message || this.$t('addComplaint.fetchRoomsError'))
      } finally {
        this.roomLoading = false
      }
    },
    handleRoomPageChange(page) {
      this.roomPage = page
      this.loadRooms()
    },
    chooseRoom(room) {
      this.form.roomId = room.roomId
      this.form.roomName = this.formatRoomName(room)
      this.roomPickerVisible = false
      this.$refs.form && this.$refs.form.validateField('roomId')
    },
    formatRoomName(room) {
      return room.roomName || [room.floorNum, room.unitNum, room.roomNum].filter(Boolean).join('-')
    },
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        try {
          this.loading = true
          await saveComplaint(this.form)
          this.$message.success(this.$t('common.operationSuccess'))
          this.$emit('success')
          this.visible = false
        } catch (error) {
          this.$message.error(error.message || this.$t('addComplaint.saveError'))
        } finally {
          this.loading = false
        }
      })
    },
    handleClose() {
      this.$refs.form && this.$refs.form.resetFields()
      this.roomPickerVisible = false
      this.roomSearch = {
        roomNumLike: '',
        ownerNameLike: '',
        linkLike: ''
      }
      this.roomList = []
      this.roomTotal = 0
    },
    headerCellStyle() {
      return { background: '#EBF2FA', color: '#364153', fontSize: '14px', fontWeight: '400', fontFamily: 'PingFang SC', height: '50px' }
    },
    cellStyle({ row }) {
      const rowIndex = this.roomList.indexOf(row)
      return { background: rowIndex % 2 === 1 ? '#F8FBFF' : '#FFFFFF', color: '#1D2129', fontSize: '14px', fontFamily: 'PingFang SC', height: '50px' }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .complaint-modal {
  .el-dialog__header {
    display: none;
  }
  .el-dialog__body {
    padding: 0;
  }
  .el-dialog__footer {
    padding: 24px;
    border-top: 1px solid #EBF2FA;
  }
}

.modal-header {
  height: 66px;
  background: #EBF2FA;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  border-bottom: 1px solid #DCE6F5;

  .modal-title {
    font-size: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 600;
    color: #1D2129;
  }
}

.complaint-form {
  padding: 24px 24px 0;

  ::v-deep .el-form-item__label {
    font-size: 14px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 500;
    color: #1D2129;
    text-align: right;
    padding-right: 8px;

    &::before {
      color: #FB2C36;
      margin-right: 2px;
    }
  }

  ::v-deep .el-input__inner,
  ::v-deep .el-textarea__inner {
    border-radius: 10px;
    border: 1px solid #DCE6F5;
    font-size: 14px;
    font-family: 'Inter', 'PingFang SC', sans-serif;
    color: #1D2129;

    &::placeholder {
      color: #8C9BB4;
    }

    &:focus {
      border-color: #0E76FF;
    }
  }

  ::v-deep .el-input__inner {
    height: 38px;
    line-height: 38px;
  }

  ::v-deep .room-select-input .el-input__inner {
    cursor: pointer;
    padding-right: 34px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .cancel-btn {
    width: 98px;
    height: 38px;
    border-radius: 10px;
    border: 1px solid #0E76FF;
    background: #FFFFFF;
    color: #0E76FF;
    font-size: 14px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 500;

    &:hover {
      background: #E8F3FF;
    }
  }

  .save-btn {
    width: 98px;
    height: 38px;
    border-radius: 10px;
    background: #0E76FF;
    border: none;
    color: #FFFFFF;
    font-size: 14px;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 500;

    &:hover {
      background: #1C99FF;
    }
  }
}

::v-deep .complaint-room-picker-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    height: 66px;
    padding: 0 24px;
    background: #EBF2FA;
    display: flex;
    align-items: center;
  }

  .el-dialog__title {
    color: #1D2129;
    font: 600 16px/22px 'PingFang SC', sans-serif;
  }

  .el-dialog__body {
    padding: 24px;
  }
}

.room-picker-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 18px;
}

.room-picker-search {
  width: 180px;
}

::v-deep .room-picker-search .el-input__inner {
  height: 38px;
  border-radius: 10px;
  border-color: #DCE6F5;
}

.room-picker-search-btn {
  height: 38px;
  border-radius: 10px;
  border-color: #0E76FF;
  color: #0E76FF;
  background: #FFFFFF;
  font: 500 14px/20px 'PingFang SC', sans-serif;
}

.room-picker-search-btn i {
  margin-right: 4px;
}

.room-picker-table {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.room-picker-choose {
  color: #0E76FF;
  cursor: pointer;
}

.room-picker-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}

.pagination-total {
  color: #1D2129;
  font: 500 14px/22px 'PingFang SC', sans-serif;
}
</style>
